import { getNotificationType } from '../constants/notificationTypes';
import notificationSoundService from '../services/notificationSoundService';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.onNotification = () => {};
    this.onAllNotifications = () => {};
    this.onNotificationRead = () => {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 1000;
  }

  connect(userId, mda_name) {
    try {
      console.log("Attempting WebSocket connection for:", userId, mda_name);
      // Connect to the same port as the backend server
      this.socket = new WebSocket("ws://localhost:45458");
      this.setupEventHandlers(userId, mda_name);
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      this.handleConnectionError();
    }
  }

  setupEventHandlers(userId, mda_name) {
    this.socket.onopen = () => {
      console.log("Connected to WebSocket server");
      this.reconnectAttempts = 0;
      
      // Send connection message
      console.log("Sending connect message for user:", userId);
      this.socket.send(JSON.stringify({ type: "connect", userId, mda_name }));
      
      // Fetch notifications
      console.log("Requesting notifications for user:", userId);
      this.socket.send(JSON.stringify({ type: "fetchNotifications", userId, mda_name }));
    };

    this.socket.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);
      const data = JSON.parse(event.data);
      
      if (data.type === "notification") {
        this.handleNewNotification(data.notification);
      } else if (data.type === "allNotifications") {
        console.log("All notifications received:", data.notifications);
        this.onAllNotifications(data.notifications);
      } else if (data.type === "notificationRead") {
        console.log("Notification marked as read:", data.id);
        this.onNotificationRead(data.id);
      }
    };

    this.socket.onclose = () => {
      console.log("WebSocket disconnected");
      this.attemptReconnect(userId, mda_name);
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  }

  handleNewNotification(notification) {
    // Enhance notification with priority and type
    const enhancedNotification = this.enhanceNotification(notification);
    
    // Play notification sound based on priority
    const config = this.getNotificationConfig(enhancedNotification);
    
    // Play sound asynchronously to avoid blocking
    setTimeout(() => {
      notificationSoundService.playSound(config.sound);
    }, 100);
    
    // Trigger callback
    this.onNotification(enhancedNotification);
  }

  enhanceNotification(notification) {
    // Determine notification priority based on content
    const priority = this.determineNotificationPriority(notification);
    
    return {
      ...notification,
      priority,
      category: this.determineNotificationCategory(notification),
      id: notification.id || Date.now() + Math.random()
    };
  }

  determineNotificationPriority(notification) {
    const message = (notification.message || '').toLowerCase();
    const type = (notification.notification_type || '').toLowerCase();
    
    // Check for urgent keywords
    if (message.includes('urgent') || message.includes('immediate') || 
        type.includes('urgent') || type.includes('critical')) {
      return 'URGENT';
    }
    
    // Check for important keywords
    if (message.includes('approval') || message.includes('release') || 
        type.includes('approval') || type.includes('release')) {
      return 'IMPORTANT';
    }
    
    // Check for success keywords
    if (message.includes('approved') || message.includes('completed') || 
        message.includes('success') || type.includes('success')) {
      return 'SUCCESS';
    }
    
    return 'INFO';
  }

  determineNotificationCategory(notification) {
    const message = (notification.message || '').toLowerCase();
    const type = (notification.notification_type || '').toLowerCase();
    
    if (message.includes('approval') || type.includes('approval')) {
      return 'approval';
    }
    
    if (message.includes('release') || type.includes('release')) {
      return 'release';
    }
    
    if (message.includes('payment') || type.includes('payment')) {
      return 'payment';
    }
    
    if (message.includes('system') || type.includes('system')) {
      return 'system';
    }
    
    return 'general';
  }

  getNotificationConfig(notification) {
    const NOTIFICATION_TYPES = {
      URGENT: { sound: 'urgent' },
      IMPORTANT: { sound: 'important' },
      INFO: { sound: 'info' },
      SUCCESS: { sound: 'success' }
    };
    
    return NOTIFICATION_TYPES[notification.priority] || NOTIFICATION_TYPES.INFO;
  }

  attemptReconnect(userId, mda_name) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts);
      
      console.log(`Reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);
      
      setTimeout(() => {
        this.connect(userId, mda_name);
      }, delay);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  handleConnectionError() {
    console.warn('WebSocket connection failed, falling back to polling');
    // Could implement polling fallback here
  }

  subscribeToNotifications(callback) {
    this.onNotification = callback;
  }

  subscribeToAllNotifications(callback) {
    this.onAllNotifications = callback;
  }

  notificationRead(callback) {
    this.onNotificationRead = callback;
  }

  sendNotification(notification) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(
        JSON.stringify({ type: "sendNotification", notification })
      );
    }
  }

  markAsRead(notificationId) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: "markAsRead", notificationId }));
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
}

export default new WebSocketService();
